import {
  ClientAccountTypeEnum,
  GiftCardBillingType,
  GiftCardOccasionType,
  InvoiceSourceTypeEnum,
} from "@/__generated__/graphql";
import { useCampaignId } from "@/components/hooks/use-campaign-id";
import { useRoute } from "@/components/shared/router";
import { showToast } from "@/components/ui/toast/show-toast";
import { GIFT_CARD_OCCASIONS } from "@/containers/profile/account-gift-card/account-gift-card-history-component/lib";
import { useCreateGiftCardBatchMutation } from "@/gqlhooks/gift-card/useCreateGiftCardBatchMutation";
import { useCreateGiftCardMutation } from "@/gqlhooks/gift-card/useCreateGiftCardMutation";
import { sendAnalyticData } from "@/lib/monitoring/analytics";
import { useAuthState } from "@/store/auth";
import { useInvoiceState } from "@/store/invoices";

import type { GiftCardFormData } from "./use-gift-card-form";

const occasionTypeMap: Record<string, GiftCardOccasionType> = Object.fromEntries(
  GIFT_CARD_OCCASIONS.map((occasion) => [occasion.key, occasion.occasionType]),
);

export function useSubmitGiftCard() {
  const clientId = useAuthState((state) => state.data.userInfo.id);
  const accountType = useAuthState((state) => state.data.userInfo.accountType);
  const isCorporate = accountType === ClientAccountTypeEnum.Corporate;
  const billingType = isCorporate ? GiftCardBillingType.Postpaid : GiftCardBillingType.Prepaid;
  const { push } = useRoute();
  const campaignId = useCampaignId();
  const [createGiftCard] = useCreateGiftCardMutation();
  const [createGiftCardBatch] = useCreateGiftCardBatchMutation();

  const onSubmit = async (data: GiftCardFormData) => {
    if (!clientId) {
      showToast({ title: "You need to be logged in.", type: "error" });
      return;
    }

    const occasionType = occasionTypeMap[data.occasion] ?? GiftCardOccasionType.General;

    try {
      if (isCorporate) {
        const result = await createGiftCardBatch({
          variables: {
            input: {
              clientId,
              amount: Number(data.amount),
              quantity: Number(data.quantity),
              occasionType,
              senderName: data.yourName,
              messageTitle: data.title,
              messageBody: data.message,
              ...(data.expirationPeriod ? { expirationPeriod: Number(data.expirationPeriod) } : {}),
            },
          },
        });

        const giftCardBatch = result.data?.createGiftCardBatch?.giftCardBatch;
        const expectedErrors = result.data?.createGiftCardBatch?.expectedErrors;

        if (expectedErrors?.length) {
          showToast({ title: expectedErrors[0].message, type: "error" });
          return;
        }

        sendAnalyticData({
          name: "submit_gift_card",
          values: {
            account_type: accountType,
            currency: "SGD",
            value: Number(data.amount) * Number(data.quantity),
            campaign_id: campaignId ?? null,
          },
        });

        if (giftCardBatch?.id) {
          showToast({
            title: "Gift card batch created successfully!",
            type: "success",
          });
        } else {
          showToast({ title: "Gift card processed.", type: "success" });
        }
        push({ pageKey: "giftCardHistory" });
        return;
      }

      const result = await createGiftCard({
        variables: {
          input: {
            clientId,
            amount: Number(data.amount),
            billingType,
            occasionType,
            senderName: data.yourName,
            messageTitle: data.title,
            messageBody: data.message,
          },
        },
      });

      const giftCard = result.data?.createGiftCard?.giftCard;
      const invoiceId = giftCard?.invoice?.id;

      sendAnalyticData({
        name: "submit_gift_card",
        values: {
          account_type: accountType,
          currency: "SGD",
          value: Number(data.amount),
          ...(invoiceId ? { invoice_id: invoiceId } : {}),
          campaign_id: campaignId ?? null,
        },
      });

      if (invoiceId) {
        useInvoiceState.getState().setInvoiceSourceType(InvoiceSourceTypeEnum.GiftCardPurchase);
        push({ pageKey: "invoiceDetail", params: { id: invoiceId } });
      } else if (giftCard?.id) {
        showToast({
          title: "Gift card created successfully!",
          type: "success",
        });
        push({ pageKey: "giftCardDetail", params: { id: giftCard.id } });
      } else {
        showToast({ title: "Gift card processed.", type: "success" });
        push({ pageKey: "giftCardHistory" });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Something went wrong. Please try again.";
      showToast({ title: message, type: "error" });
    }
  };

  return { onSubmit };
}
